import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateVenueInput,
  VenueInclusion,
  UpsertVenueReviewInput,
  Venue,
  VenueReview,
  VenueStats,
} from '@guabi/shared';
import { api, apiDelete, apiGet, apiPatch, apiPost, apiPut } from '@/lib/api';

export const venuesQueryKey = (wedId: string) => ['venues', wedId] as const;

export interface VenuesResponse {
  items: Venue[];
  /** Every review for every venue — one query serves the whole tab. */
  reviews: VenueReview[];
  stats: VenueStats;
}

export function useVenues(wedId: string) {
  return useQuery({
    queryKey: venuesQueryKey(wedId),
    queryFn: () => apiGet<VenuesResponse>(`/weddings/${wedId}/venues`),
  });
}

/** A venue or a review changing moves the card grid and the Home tab together. */
function useVenueMutation<TArgs, TResult = void>(
  fn: (args: TArgs) => Promise<TResult>,
  wedId: string,
) {
  const qc = useQueryClient();
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: venuesQueryKey(wedId) });
      void qc.invalidateQueries({ queryKey: ['summary', wedId] });
    },
  });
}

export function useCreateVenue(wedId: string) {
  return useVenueMutation<CreateVenueInput, Venue>(
    (input) => apiPost<Venue>(`/weddings/${wedId}/venues`, input),
    wedId,
  );
}

export function useUpdateVenue(wedId: string) {
  return useVenueMutation<
    { venueId: string; input: Partial<CreateVenueInput> & { updatedAt: string } },
    Venue
  >(({ venueId, input }) => apiPatch<Venue>(`/weddings/${wedId}/venues/${venueId}`, input), wedId);
}

/**
 * Saves one field and writes the answer straight into the cache.
 *
 * Deliberately not built on `useVenueMutation`, which invalidates the venue list
 * and the home summary on every success — two refetches per save is fine when a
 * save is a button press, and wrong when it is something a textarea does every
 * few seconds while you type. The response is the updated venue, so patching it
 * in is both cheaper and steadier than asking for the list again.
 *
 * The list is still reconciled: the drawer invalidates once on close.
 */
export function usePatchVenueField(wedId: string) {
  const qc = useQueryClient();
  return useMutation<
    Venue,
    Error,
    { venueId: string; input: Partial<CreateVenueInput> & { updatedAt: string } }
  >({
    mutationFn: ({ venueId, input }) =>
      apiPatch<Venue>(`/weddings/${wedId}/venues/${venueId}`, input),
    onSuccess: (saved) => {
      qc.setQueryData<VenuesResponse>(venuesQueryKey(wedId), (prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((v) => (v.venueId === saved.venueId ? saved : v)),
            }
          : prev,
      );
    },
  });
}

export function useDeleteVenue(wedId: string) {
  return useVenueMutation<{ venueId: string }>(
    ({ venueId }) => apiDelete(`/weddings/${wedId}/venues/${venueId}`),
    wedId,
  );
}

export interface VenueProposal {
  fields: Partial<CreateVenueInput>;
  /** Ready-composed notes: the venue's own words, and anything it said about money. */
  notes?: string;
  inclusions: VenueInclusion[];
  /** What the page said about money, as words. Never a number, never a quote. */
  priceHint?: string;
  issues: { field: string; message: string }[];
  sourceUrl?: string;
}

/**
 * Reads a link or a screenshot into a proposal. **Writes nothing**, so it is a
 * plain mutation and only the ai-usage figure needs refreshing afterwards.
 */
export function useExtractVenue(wedId: string) {
  const qc = useQueryClient();
  return useMutation<
    VenueProposal,
    Error,
    { url: string } | { image: { mediaType: string; data: string } } | { pdf: { data: string } }
  >({
    mutationFn: (body) =>
      api<VenueProposal>(`/weddings/${wedId}/ai/extract-venue`, {
        method: 'POST',
        body: JSON.stringify(body),
        // Inside the Lambda's 29s, so a slow read aborts here with something
        // specific rather than arriving as an unparseable gateway error.
        timeoutMs: 28_000,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ai-usage', wedId] });
    },
  });
}

/**
 * Asks for a few sentences about the comparison. Sends ids only — the server
 * works the numbers out again, because a paragraph built on totals a client
 * invented would read exactly as convincingly as a true one.
 */
export function useCompareNarrative(wedId: string) {
  const qc = useQueryClient();
  return useMutation<{ text: string }, Error, { venueIds: string[] }>({
    mutationFn: (body) =>
      api<{ text: string }>(`/weddings/${wedId}/ai/compare-venues`, {
        method: 'POST',
        body: JSON.stringify({ ...body, locale: navigator.language }),
        timeoutMs: 28_000,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ai-usage', wedId] });
    },
  });
}

/** Upserts the caller's own review. The server keys it by their id. */
export function useUpsertMyReview(wedId: string) {
  return useVenueMutation<{ venueId: string; input: UpsertVenueReviewInput }, VenueReview>(
    ({ venueId, input }) =>
      apiPut<VenueReview>(`/weddings/${wedId}/venues/${venueId}/review`, input),
    wedId,
  );
}

export function useDeleteMyReview(wedId: string) {
  return useVenueMutation<{ venueId: string }>(
    ({ venueId }) => apiDelete(`/weddings/${wedId}/venues/${venueId}/review`),
    wedId,
  );
}
